import { gsap } from "gsap";

export interface HorizontalLoopConfig {
  repeat?: number;
  paused?: boolean;
  speed?: number;
  snap?: boolean | number | ((value: number) => number);
  paddingRight?: number | string;
  reversed?: boolean;
}

export interface HorizontalLoopTimeline extends gsap.core.Timeline {
  next: (vars?: gsap.TweenVars) => gsap.core.Tween;
  previous: (vars?: gsap.TweenVars) => gsap.core.Tween;
  current: () => number;
  toIndex: (index: number, vars?: gsap.TweenVars) => gsap.core.Tween;
  times: number[];
}

export function horizontalLoop(
  items: Element[] | NodeListOf<Element> | string,
  config: HorizontalLoopConfig = {}
): HorizontalLoopTimeline {
  const elements = gsap.utils.toArray<Element>(items);
  const length = elements.length;
  if (length === 0) throw new Error("No items provided to horizontalLoop.");

  const tl = gsap.timeline({
    repeat: config.repeat,
    paused: config.paused,
    defaults: { ease: "none" },
    onReverseComplete: function () {
      tl.totalTime(tl.rawTime() + tl.duration() * 100);
    },
  }) as HorizontalLoopTimeline;

  const times: number[] = [];
  const widths: number[] = [];
  const xPercents: number[] = [];
  let curIndex = 0;
  const pixelsPerSecond = (config.speed ?? 1) * 100;

  // Fix for the TypeScript error - properly handle snap function creation
  const snapFunc = (() => {
    if (config.snap === false) {
      return (v: number) => v;
    }

    if (typeof config.snap === "function") {
      return config.snap;
    }

    // For number or undefined, use gsap.utils.snap with the number or default 1
    const snapValue = typeof config.snap === "number" ? config.snap : 1;
    return (v: number) => {
      const snapper = gsap.utils.snap(snapValue);
      return snapper(v);
    };
  })();

  // Set initial xPercent and widths
  gsap.set(elements, {
    xPercent: (i: number, el: Element) => {
      const w = (widths[i] = parseFloat(
        gsap.getProperty(el, "width", "px") as string
      ));
      xPercents[i] = snapFunc(
        (parseFloat(gsap.getProperty(el, "x", "px") as string) / w) * 100 +
          (gsap.getProperty(el, "xPercent") as number)
      );
      return xPercents[i];
    },
  });
  gsap.set(elements, { x: 0 });

  const startX = elements[0].getBoundingClientRect().left + window.scrollX;
  const last = elements[length - 1];
  const lastRect = last.getBoundingClientRect();
  const totalWidth =
    lastRect.left +
    window.scrollX +
    (xPercents[length - 1] / 100) * widths[length - 1] -
    startX +
    lastRect.width * (gsap.getProperty(last, "scaleX") as number) +
    (typeof config.paddingRight === "number"
      ? config.paddingRight
      : parseFloat(config.paddingRight as string) || 0);

  for (let i = 0; i < length; i++) {
    const item = elements[i];
    const rect = item.getBoundingClientRect();
    const curX = (xPercents[i] / 100) * widths[i];
    const distanceToStart = rect.left + window.scrollX + curX - startX;
    const scaleX = gsap.getProperty(item, "scaleX") as number;
    const distanceToLoop = distanceToStart + widths[i] * scaleX;

    tl.to(
      item,
      {
        xPercent: snapFunc(((curX - distanceToLoop) / widths[i]) * 100),
        duration: distanceToLoop / pixelsPerSecond,
      },
      0
    )
      .fromTo(
        item,
        {
          xPercent: snapFunc(
            ((curX - distanceToLoop + totalWidth) / widths[i]) * 100
          ),
        },
        {
          xPercent: xPercents[i],
          duration:
            (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false,
        },
        distanceToLoop / pixelsPerSecond
      )
      .add("label" + i, distanceToStart / pixelsPerSecond);

    times[i] = distanceToStart / pixelsPerSecond;
  }

  function toIndex(index: number, vars?: gsap.TweenVars) {
    vars = vars || {};
    if (Math.abs(index - curIndex) > length / 2) {
      index += index > curIndex ? -length : length;
    }
    const newIndex = gsap.utils.wrap(0, length, index);
    let time = times[newIndex];
    // Fix comparison operator precedence with parentheses
    if (time > tl.time() !== index > curIndex) {
      vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
      time += tl.duration() * (index > curIndex ? 1 : -1);
    }
    curIndex = newIndex;
    vars.overwrite = true;
    return tl.tweenTo(time, vars);
  }

  tl.next = (vars?: gsap.TweenVars) => toIndex(curIndex + 1, vars);
  tl.previous = (vars?: gsap.TweenVars) => toIndex(curIndex - 1, vars);
  tl.current = () => curIndex;
  tl.toIndex = (index: number, vars?: gsap.TweenVars) => toIndex(index, vars);
  tl.times = times;
  tl.progress(1, true).progress(0, true);

  if (config.reversed) {
    tl.vars.onReverseComplete?.();
    tl.reverse();
  }

  return tl;
}
